import { Drawer, Grid, Typography, TextField as MUITextField, Divider } from '@material-ui/core';
import { NumberField, Button, AutoComplete, SmSpace, TextField } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { InvestidorResponse, InvestidorPrivado, BancoResponse, InstrumentoFinanceiro } from 'common/genericTypes';
import { addCpfCnpjMask } from 'utils/mask';
import { stripCpfCnpjFormatting } from 'utils/validation';
import { getQuantityType } from 'utils/instrumentoFinanceiroUtils';
import { formatNumber } from 'utils/number';

interface Props {
  custodianteRazaoSocial?: string;
  investidores: Array<InvestidorPrivado>;
  investidoresOptions: Array<InvestidorResponse>;
  bancosOptions: Array<BancoResponse>;
  valorUnitario: number;
  quantidadeDisponivel: number;
  open: boolean;
  onClose: () => void;
  addInvestidor: (value: InvestidorPrivado) => void;
  tipoInstrumento?: InstrumentoFinanceiro;
}

const InvestidoresDrawer: React.FC<Props> = (props) => {
  const {
    custodianteRazaoSocial,
    investidores,
    investidoresOptions,
    bancosOptions,
    valorUnitario,
    quantidadeDisponivel,
    open,
    addInvestidor,
    onClose,
    tipoInstrumento
  } = props;

  const [quantidade, setQuantidade] = useState<number>(0);
  const [investidor, setInvestidor] = useState<InvestidorResponse>();
  const [banco, setBanco] = useState<BancoResponse | null>(null);
  const [agencia, setAgencia] = useState<string>('');
  const [conta, setConta] = useState<string>('');

  useEffect(() => {
    if (bancosOptions && bancosOptions.length > 0 && investidor) {
      const bancoAntigo = bancosOptions.find(
        (_) => _.ispb === investidor.ispbBanco && _.shortname === investidor.razaoSocialBanco
      );
      if (bancoAntigo) {
        setBanco(bancoAntigo);
        setAgencia(investidor.agenciaBanco);
        setConta(investidor.contaBanco);
      }
    }
  }, [bancosOptions, investidor]);

  const bancosFilter = (value: string) =>
    bancosOptions.filter(
      (b) =>
        b.code.includes(value) ||
        b.fullname.toLocaleLowerCase().includes(value) ||
        b.ispb.includes(value) ||
        b.shortname.toLocaleLowerCase().includes(value)
    );

  const handleAddClick = () => {
    if (investidor && quantidade && banco && agencia && conta) {
      const investidorPrivado: InvestidorPrivado = {
        cpfCnpj: investidor.cpfCnpj,
        razaoSocial: investidor.razaoSocial,
        investidorId: investidor.id,
        quantidade: quantidade,
        ispbBanco: banco.ispb,
        razaoSocialBanco: banco.shortname,
        agenciaBanco: agencia,
        contaBanco: conta,
      };
      addInvestidor(investidorPrivado);
      handleClose();
    }
  };

  const handleClose = () => {
    setQuantidade(0);
    setInvestidor(undefined);
    setBanco(null);
    setAgencia('');
    setConta('');
    onClose();
  };

  return (
    <Drawer open={open} onClose={handleClose} PaperProps={{ style: { padding: '40px', width: 465 } }} anchor="right">
      <Typography variant="h3">{'Adicionar Investidor'}</Typography>
      <Typography variant="body2">{custodianteRazaoSocial}</Typography>
      <SmSpace />
      <Divider variant="fullWidth" />
      <SmSpace />
      <Grid container spacing={2} style={{ width: '100%' }}>
        <Grid item xs={12}>
          <AutoComplete
            label={'CPF/CNPJ Investidor *'}
            options={investidoresOptions.filter((_) => !investidores.some((i) => i.investidorId === _.id))}
            getOptionLabel={(option: InvestidorResponse) => addCpfCnpjMask(option.cpfCnpj)}
            value={investidor}
            onChange={(newValue) => setInvestidor(newValue)}
            filterOptions={(options: Array<InvestidorResponse>, state) =>
              options.filter((_) => stripCpfCnpjFormatting(_.cpfCnpj).includes(stripCpfCnpjFormatting(state.inputValue)))
            }
            renderOption={(option: InvestidorResponse) => (
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex' }}>
                  <Typography style={{ margin: 'auto 12px' }}>{addCpfCnpjMask(option.cpfCnpj)}</Typography>
                </div>
                <div>
                  <Typography style={{ margin: 'auto 12px' }} variant="body2">
                    {option.razaoSocial}
                  </Typography>
                </div>
              </div>
            )}
            renderInput={(params) => (
              <MUITextField
                {...params}
                label={'CPF/CNPJ Investidor (origem) *'}
                InputProps={{ ...params.InputProps }}
                variant="outlined"
              />
            )}
            helperText={investidor?.razaoSocial}
          />
        </Grid>
        <Grid item xs={12}>
          <NumberField
            label={'Quantidade'}
            required
            value={quantidade}
            numberType={getQuantityType(tipoInstrumento)}
            onChange={(value) => setQuantidade(value ?? 0)}
            helperText={`Quantidade Disponível: ${formatNumber(Math.max(0, quantidadeDisponivel), { minimumFractionDigits: 0, maximumFractionDigits: 8 })}`}
          />

        </Grid>
        <Grid item xs={12}>
          <NumberField
            label={'Volume Total'}
            disabled
            value={valorUnitario * quantidade}
            numberType="eightDecimals"
            onChange={() => null}
          />
        </Grid>
        <Grid item xs={12}>
          <AutoComplete
            label={'Banco *'}
            options={bancosOptions ?? []}
            getOptionLabel={(option: BancoResponse) => `${option.code} - ${option.shortname}`}
            value={banco || null}
            onChange={(newValue) => setBanco(newValue)}
            filterOptions={
              bancosOptions
                ? (_options: Array<BancoResponse>, state) => bancosFilter(state.inputValue.toLocaleLowerCase())
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TextField label={'Agência'} required value={agencia} onChange={(value) => setAgencia(value)} />
        </Grid>
        <Grid item xs={12}>
          <TextField label={'Conta'} required value={conta} onChange={(value) => setConta(value)} />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={quantidade === null ||
              quantidade <= 0 ||
              !investidor ||
              !banco ||
              !agencia ||
              !conta ||
              (quantidade > Number((quantidadeDisponivel + 1e-8).toFixed(8)))}
            onClick={handleAddClick}
          >
            {'Adicionar Investidor'}
          </Button>

        </Grid>
        <Grid item xs={12}>
          <Button fullWidth variant="outlined" onClick={handleClose}>
            {'Fechar'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default InvestidoresDrawer;
